import { ChakraProvider, createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";
import {
  ColorModeProvider,
  type ColorModeProviderProps,
} from "@/components/common/color-mode";

import { buttonRecipe } from "@/recipes/button";

export const config = defineConfig({
  // Enables a CSS reset for consistent styling across browsers.
  preflight: true,

  // Where all design values are defined.
  theme: {
    // 1. RAW TOKENS: The foundational values of your design system.
    tokens: {
      colors: {
        transparent: { value: 'transparent' },
        current: { value: 'currentColor' },
        white: { value: '#ffffff' },

        // Your Orange/CTA palette
        orange: {
          vibrant: { value: '#FF6B6B' },
          bright: { value: '#FFA84C' },
          sunny: { value: '#FFD93D' },
        },

        // Your Blue/Primary Action palette
        blue: {
          primary: { value: '#2574f5' },
          dark: { value: '#051f3d' },
          darkest: { value: '#060b17' },
        },
        // Your Grays and Off-Whites
        gray: {
          100: { value: '#f7f7f7' }, // text/icons on dark
          200: { value: '#f5f6fa' }, // light bg 1
          300: { value: '#e4e7ed' }, // minor bg for buttons/icons
          400: { value: '#ececec' }, // light button 2
          500: { value: '#83899c' }, // subtle text
        },

        // Your Darks and Off-Blacks
        black: {
          100: { value: '#2a2e3b' }, // light text main
          200: { value: '#161922' }, // light text main 2 / dark bg 1
          300: { value: '#11141e' }, // logged in header dark
          400: { value: '#0a0d15' }, // dark bg 2
        },

        red: {
          500: { value: '#EF4444' },
          600: { value: '#DC2626' },
        },

      },
      fonts: {
        heading: { value: `'Manrope', 'Figtree', sans-serif` },
        body: { value: `'Inter', sans-serif` },
      },
      fontSizes: {
        xs: { value: '0.75rem' },
        sm: { value: '0.875rem' },
        md: { value: '1rem' },
        lg: { value: '1.125rem' },
        xl: { value: '1.25rem' },
        '2xl': { value: '1.5rem' },
        '3xl': { value: '1.875rem' },
        '4xl': { value: '2.25rem' },
        '5xl': { value: '3rem' },
      },
      fontWeights: {
        normal: { value: '400' },
        medium: { value: '500' },
        bold: { value: '700' },
      },
      radii: {
        sm: { value: '4px' },
        md: { value: '6px' },
        lg: { value: '8px' },
        full: { value: '9999px' },
      },
      spacing: {
        // Example spacing tokens, useful for padding, margin, height, etc.
        '1': { value: '0.25rem' }, '2': { value: '0.5rem' }, '3': { value: '0.75rem' }, '4': { value: '1rem' }, '5': { value: '1.25rem' }, '6': { value: '1.5rem' }, '8': { value: '2rem' }, '10': { value: '2.5rem' }, '12': { value: '3rem' },
      },
    },

    // 2. SEMANTIC TOKENS: Maps raw tokens to their purpose. This is where theming happens.
    semanticTokens: {
      colors: {
        // ---- Backgrounds ----
        bg: {
          canvas: { value: { base: '{colors.gray.200}', _dark: '{colors.black.400}' } },
          surface: { value: { base: '{colors.white}', _dark: '{colors.black.200}' } },
          header: { value: { base: '{colors.white}', _dark: '{colors.black.300}' } },
          sidebar: { value: { base: '{colors.white}', _dark: '{colors.black.300}' } },
        },

        // ---- Text ----
        text: {
          primary: { value: { base: '{colors.black.200}', _dark: '{colors.gray.100}' } },
          body: { value: { base: '{colors.black.100}', _dark: '{colors.gray.100}' } },
          subtle: { value: { base: '{colors.gray.500}', _dark: '{colors.gray.500}' } },
          hero: { value: { base: '{colors.black.200}', _dark: '{colors.white}' } },
          onAccent: { value: '{colors.white}' }, // Text for on top of accent colors
          accent: { value: { base: '{colors.blue.primary}', _dark: '{colors.blue.primary}' } },
          danger: { value: { base: '{colors.red.600}', _dark: '{colors.red.500}' } },
        },

        // ---- Borders & Icons ----
        border: {
          primary: { value: { base: '{colors.gray.300}', _dark: '{colors.black.300}' } },
        },
        icon: {
          primary: { value: { base: '{colors.black.100}', _dark: '{colors.gray.100}' } },
        },

        // ---- Buttons & Actions ----
        button: {
          // Primary Action Button (Blue)
          primary: {
            background: { value: '{colors.blue.primary}' },
            text: { value: '{colors.white}' },
            hoverBackground: { value: '{colors.blue.dark}' }
          },
          // Secondary Action Button (Gray)
          secondary: {
            background: { value: { base: '{colors.gray.400}', _dark: '{colors.black.300}' } },
            text: { value: { base: '{colors.black.100}', _dark: '{colors.gray.100}' } },
            hoverBackground: { value: { base: '{colors.gray.300}', _dark: '{colors.black.200}' } }
          },
          // Call-to-Action Gradient Button (Orange)
          cta: {
            gradient: { value: 'linear-gradient(to right, {colors.orange.vibrant}, {colors.orange.bright}, {colors.orange.sunny})' },
            text: { value: '{colors.white}' }
          }
        }
      }
    },

    // You can also define text styles and component recipes here
    textStyles: {
      h1: {
        description: 'Heading 1 style',
        value: {
          fontSize: '5xl', fontWeight: 'bold', fontFamily: 'heading',
        },
      },
      h2: {
        description: 'Heading 2 style',
        value: {
          fontSize: '4xl', fontWeight: 'bold', fontFamily: 'heading',
        },
      },
      h3: {
        description: 'Heading 3 style',
        value: {
          fontSize: '2xl', fontWeight: 'bold', fontFamily: 'heading',
        },
      },
      body: {
        description: 'Body text style',
        value: {
          fontSize: 'md', fontWeight: 'normal', fontFamily: 'body',
        },
      },
      subtle: {
        description: 'Subtle text style for helpers or captions',
        value: {
          fontSize: 'sm', fontFamily: 'body', color: 'text.subtle',
        },
      }
    },

    // 4. RECIPES: Multi-variant styles for components like buttons.
    recipes: {
      button: buttonRecipe,
    },
  },

  // 3. GLOBAL CSS: Base styles applied to the entire application.
  globalCss: {
    'body': {
      bg: 'bg.canvas', // Uses semantic token
      color: 'text.body', // Uses semantic token
      fontFamily: 'body',
      transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
    },
    'h1, h2, h3, h4, h5, h6': {
      fontFamily: 'heading',
      color: 'text.primary',
    },
    'a': {
      color: 'text.accent',
      textDecoration: 'none',
      _hover: {
        textDecoration: 'underline',
      },
    }
  },
});

const system = createSystem(defaultConfig, config)

export function ThemeProvider(props: ColorModeProviderProps) {
  return (
    <ChakraProvider value={system}>
      <ColorModeProvider>{props.children}</ColorModeProvider>
    </ChakraProvider>
  )
}
